import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const WARNING_SECONDS = 10 * 60

export default class extends Controller {
  static targets = ["countdown"]
  static values = { seconds: Number, url: String }

  initialize() {
    this.requestedAt = new Set()
  }

  connect() {
    this.requestedAt.clear()
    this.start()
  }

  disconnect() {
    this.stop()
    this.abortRequest()
  }

  secondsValueChanged(seconds) {
    this.secondsLeft = seconds
    if (!this.timer) return

    this.render()
    if (seconds === WARNING_SECONDS) this.refreshCart()
  }

  start() {
    this.stop()
    this.tick()
    this.timer = window.setInterval(() => this.tick(), 1000)
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer)
    if (this.expirationTimer) window.clearTimeout(this.expirationTimer)
    this.timer = null
    this.expirationTimer = null
  }

  tick() {
    this.render()

    if (this.secondsLeft === WARNING_SECONDS) this.refreshCart()
    if (this.secondsLeft <= 0) {
      this.stop()
      this.expirationTimer = window.setTimeout(() => this.refreshCart(), 1000)
      return
    }

    this.secondsLeft -= 1
  }

  render() {
    const minutes = Math.floor(Math.max(this.secondsLeft, 0) / 60)
    const seconds = Math.max(this.secondsLeft, 0) % 60
    const time = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    this.countdownTargets.forEach(countdown => countdown.textContent = time)
  }

  async refreshCart() {
    if (this.requestedAt.has(this.secondsLeft) || !this.hasUrlValue) return

    const requestSecond = this.secondsLeft
    this.requestedAt.add(requestSecond)
    this.abortRequest()
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error(`Unable to refresh cart (${response.status})`)
      if (response.status === 204) return

      const data = await response.json()
      if (data.redirect_url) {
        Turbo.visit(data.redirect_url)
        return
      }

      document.querySelector("#modal-extend-cart")?.remove()
      if (data.modal_extend_cart) document.body.insertAdjacentHTML("beforeend", data.modal_extend_cart)
      if ((!data.cart_active || data.replace_cart) && data.cart) document.querySelector("#header-cart")?.replaceChildren(this.fragment(data.cart))
    } catch (error) {
      if (error.name !== "AbortError") this.requestedAt.delete(requestSecond)
    } finally {
      this.abortController = null
    }
  }

  fragment(html) {
    return document.createRange().createContextualFragment(html)
  }

  abortRequest() {
    this.abortController?.abort()
    this.abortController = null
  }
}
